/**
 * 構造化データ (JSON-LD) 生成ヘルパー
 * Google検索でリッチスニペット表示を実現
 */

import { lessonOgImageUrl } from "@/lib/seo/lesson-og-image";
import type { GraphqlCourseDetail, GraphqlJob } from "@noiman/shared-graphql";
import { canEmitJobPosting, type JobFeatureFlags } from "@/lib/jobs-policy";
import { hasMissingSourceCourseDuration } from "@/lib/course-duration";
import {
  getCombinedAuthorSchema,
  organizationRef,
  ORGANIZATION_ID,
  ORGANIZATION_NAME,
  getEditorialTeamOrgSchema,
  getSupervisorPersonSchema,
  SITE_SUPERVISOR,
} from "./supervisor";
import { safeJsonLdStringify } from "@noiman/ui/seo";

const BASE_URL = "https://yumesaku.tech";
const DEFAULT_OG_IMAGE = "https://storage.chotdekiru.com/ogp/yumesaku_ogp_v2.png";

/**
 * Organization スキーマ
 * サイト全体の組織情報
 */
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    alternateName: "ゆめさくコード",
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    description:
      "IT・プログラミング学習を、理解から仕事までつなぐプラットフォーム。用語の理解、プログラミング学習、作品づくり、仕事への接続まで、学びの入口と出口をひとつにつなげます。",
    // 公式のアカウント。リポジトリに本物の住所があるものだけ (X、Instagram、note、YouTube)
    sameAs: [
      "https://x.com/yumesaku_tech",
      "https://www.instagram.com/yumesaku_lab/",
      "https://note.com/luagate",
    ],
    // ヘッダーの帯の「女性向けエンジニアスクール」(/women) と同じ。画面に出ている関係だけ書く
    subOrganization: {
      "@type": "EducationalOrganization",
      name: "ゆめさくエンジニア",
      url: `${BASE_URL}/women`,
      description: "未経験の女性のための、18ヶ月のオンライン・プログラミングスクール。",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@noiman.io",
    },
  };
}

/**
 * WebSite スキーマ
 * サイト検索機能を含む
 */
export function generateWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ゆめさくコード",
    url: BASE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * Course スキーマ
 * コース詳細ページ用
 *
 * Google Rich Results "Course info" 推奨フィールド:
 * - name / description / provider 必須
 * - hasCourseInstance / offers で詳細リッチリザルト対応
 * - numberOfCredits = lesson 数 / timeRequired = lesson 合計時間 (ISO 8601)
 */
export function generateCourseSchema(course: GraphqlCourseDetail) {
  const educationalLevelMap: Record<string, string> = {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
  };

  // 全 lesson を平坦化
  const allLessons = (course.sections ?? []).flatMap(
    (section) => section.lessons ?? [],
  );
  const lessonCount = allLessons.length;

  const knownDurationMinutes = allLessons.reduce(
    (sum, lesson) =>
      sum +
      (typeof lesson.duration === "number" && lesson.duration > 0
        ? lesson.duration
        : 0),
    0,
  );
  const durationSourceMissing = hasMissingSourceCourseDuration({
    slug: course.slug,
    lessonCount,
    estimatedHours: course.estimatedHours,
    knownDurationMinutes,
  });

  // lesson の duration (分) を合計。
  //
  // **未設定を5分に読み替えない。** 枠だけ作った講座は duration が0なので、
  // 5分換算にすると本文が1本も無い20レッスンの講座が timeRequired PT1H40M を
  // 名乗る。学習時間は約束なので、分からないなら出さない
  const totalMinutes = allLessons.reduce(
    (sum, lesson) =>
      sum + (lesson.duration && lesson.duration > 0 ? lesson.duration : 0),
    0,
  );
  // estimatedHours が DB にあればそれを優先 (時間単位)、なければ lesson 合計から算出
  const totalMinutesFromHours = course.estimatedHours
    ? course.estimatedHours * 60
    : 0;
  const effectiveMinutes = totalMinutesFromHours || totalMinutes;
  // 合計が0分なら、そもそも時間を名乗らない
  const hasDuration = effectiveMinutes > 0;
  const timeRequiredIso = minutesToIso8601Duration(effectiveMinutes);

  // teaches: section タイトルを優先しつつ、無い場合は空配列
  const teaches = (course.sections ?? [])
    .map((section) => section.title)
    .filter((title): title is string => Boolean(title));

  const isFree = course.isFree ?? true;

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: course.title,
    description: course.description,
    provider: organizationRef(),
    url: `${BASE_URL}/learn/courses/${course.slug}`,
    image: course.thumbnailUrl || DEFAULT_OG_IMAGE,
    courseCode: course.slug,
    inLanguage: "ja",
    isAccessibleForFree: isFree,
    ...(!durationSourceMissing && hasDuration && { timeRequired: timeRequiredIso }),
    educationalLevel: educationalLevelMap[course.difficultyLevel] || "Beginner",
    teaches,
    ...(lessonCount > 0 && { numberOfCredits: lessonCount }),
    offers: {
      "@type": "Offer",
      // 無料コースは price 0、有料コースは価格非開示 (luagate プラン経由のため非公開)
      ...(isFree
        ? { price: "0", priceCurrency: "JPY", category: "Free" }
        : { category: "Paid" }),
      availability: "https://schema.org/InStock",
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      ...(!durationSourceMissing && hasDuration && { courseWorkload: timeRequiredIso }),
    },
  };
}

/**
 * 分数を ISO 8601 duration ("PT5H15M") に変換
 * 0 分 → "PT0M" (schema.org 的に有効な最小値)
 */
function minutesToIso8601Duration(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes <= 0) {
    return "PT0M";
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) {
    return `PT${hours}H${minutes}M`;
  }
  if (hours > 0) {
    return `PT${hours}H`;
  }
  return `PT${minutes}M`;
}

/**
 * JobPosting スキーマ
 * 求人詳細ページ用
 */
export function generateJobPostingSchema(
  job: GraphqlJob,
  flags: JobFeatureFlags,
  now = new Date(),
) {
  if (!canEmitJobPosting(job, flags, now)) return null;

  const employmentTypes: Record<string, string> = {
    "full-time": "FULL_TIME",
    full_time: "FULL_TIME",
    "part-time": "PART_TIME",
    part_time: "PART_TIME",
    contract: "CONTRACTOR",
    internship: "INTERN",
    intern: "INTERN",
  };
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    datePosted: job.postedAt,
    validThrough: job.deadline,
    employmentType: employmentTypes[job.type] ?? "OTHER",
    url: `${BASE_URL}/jobs/${job.id}`,
    sameAs: job.canonicalSourceUrl,
    directApply: false,
    hiringOrganization: {
      "@type": "Organization",
      name: job.company?.name || "未公開",
      sameAs: job.company?.website,
      logo: job.company?.logo,
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.location || "リモート",
        addressCountry: "JP",
      },
    },
    applicantLocationRequirements: {
      "@type": "Country",
      name: "JP",
    },
  };

  // 給与情報があれば追加
  if (job.salary?.min && job.salary?.max) {
    schema.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.salary.currency || "JPY",
      value: {
        "@type": "QuantitativeValue",
        minValue: job.salary.min,
        maxValue: job.salary.max,
        unitText:
          job.salary.period?.toLowerCase() === "year"
            ? "YEAR"
            : job.salary.period?.toLowerCase() === "hour"
              ? "HOUR"
              : "MONTH",
      },
    };
  }

  return schema;
}

/**
 * Article スキーマ
 * ニュース/ブログ記事用
 */
export function generateArticleSchema(article: {
  title: string;
  description: string;
  /** 記事の公開日。無いときは欄ごと出さない。リクエスト時刻で埋めない */
  publishedDate?: string;
  modifiedDate?: string;
  author?: string;
  /** 書き手が記事ごとに決まるページは、ここで組んだ author をそのまま渡す */
  authorSchema?: unknown;
  image?: string;
  url: string;
}) {
  // author は監修者 (Person) を Google E-E-A-T 推奨形式で出力。
  // article.author が明示指定された場合のみ Organization 後方互換を維持。
  const authorSchema =
    article.authorSchema ??
    (article.author
      ? organizationRef()
      : getCombinedAuthorSchema());
  const dateModified = article.modifiedDate || article.publishedDate;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.image || DEFAULT_OG_IMAGE,
    ...(article.publishedDate ? { datePublished: article.publishedDate } : {}),
    ...(dateModified ? { dateModified } : {}),
    author: authorSchema,
    publisher: organizationRef(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.url,
    },
  };
}

/**
 * FAQPage スキーマ
 * FAQページ用
 */
export function generateFAQPageSchema(
  faqs: Array<{ question: string; answer: string }>,
) {
  // 重複する質問を除去
  const uniqueFaqs = faqs.filter(
    (faq, index, self) =>
      index === self.findIndex((f) => f.question === faq.question),
  );

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: uniqueFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/**
 * WebApplication スキーマ
 * ログイン無しでブラウザの中で動く道具 (/tools の下の頁) 用。
 * 無料で使えること、動く場所がブラウザであることを機械に伝える。
 * 日付は頁が持つ公開日と更新日を渡す。リクエスト時刻では埋めない
 */
export function generateWebApplicationSchema(app: {
  name: string;
  description: string;
  url: string;
  /** schema.org の applicationCategory (例 SecurityApplication, DeveloperApplication, UtilitiesApplication) */
  category: string;
  publishedDate: string;
  modifiedDate: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: app.name,
    description: app.description,
    url: app.url,
    applicationCategory: app.category,
    operatingSystem: "Web ブラウザ",
    browserRequirements: "JavaScript を有効にしたブラウザ",
    inLanguage: "ja",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "JPY" },
    datePublished: app.publishedDate,
    dateModified: app.modifiedDate,
    author: organizationRef(),
    publisher: organizationRef(),
  };
}

/**
 * Product スキーマ
 * ゆめさくスキル (買い切り教材) の販売LP用
 *
 * Stripe 1回払い・買い切りなので priceValidUntil は付けない。
 * 在庫概念が無い商品なので availability は常に InStock 扱い。
 */
export function generateProductSchema(product: {
  name: string;
  description: string;
  url: string;
  price: number;
  sku: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    url: product.url,
    image: DEFAULT_OG_IMAGE,
    sku: product.sku,
    brand: {
      "@type": "Brand",
      name: "ゆめさくコード",
    },
    offers: {
      "@type": "Offer",
      url: product.url,
      price: product.price,
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  };
}

/**
 * BreadcrumbList スキーマ
 * パンくずリスト用
 */
export function generateBreadcrumbSchema(
  breadcrumbs: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

/**
 * LearningResource スキーマ
 * レッスン・教材ページ用
 *
 * lesson.type に応じて schema.org の interactivityType / educationalUse /
 * learningResourceType を切り替える。
 * - coding / sql / terminal / react → 体験型 (active)
 * - quiz → 体験型 (active) Quiz
 * - text / ai (それ以外) → 解説型 (expositive)
 */
export function generateLearningResourceSchema(lesson: {
  title: string;
  description?: string;
  type: string;
  courseSlug: string;
  /** コースの共有の絵とサムネイル。og:image と同じものを image に出すために要る */
  courseOgpImageUrl?: string | null;
  courseThumbnailUrl?: string | null;
  courseName?: string;
  lessonSlug: string;
  estimatedDuration?: number;
  /** ISO 8601 公開日 (lessons.created_at) */
  datePublished?: string | null;
  /** ISO 8601 最終更新日 (lessons.updated_at) */
  dateModified?: string | null;
  /** 著者名 (省略時はゆめさくコード編集部) */
  authorName?: string;
}) {
  // schema.org の許容値に寄せた literal union で typo を防ぐ。
  // interactivityType は active / expositive / mixed のみ schema.org が定義。
  type InteractivityType = "active" | "expositive" | "mixed";
  type EducationalUse = "practice" | "instruction" | "assessment";
  type SchemaShape = {
    learningResourceType: string;
    interactivityType: InteractivityType;
    educationalUse: EducationalUse[];
  };

  const shapeForType = (type: string): SchemaShape => {
    switch (type) {
      case "coding":
      case "code":
      case "sql":
      case "terminal":
      case "react":
      case "vm":
      case "prompt":
      case "ai":
        return {
          learningResourceType: "Interactive code exercise",
          interactivityType: "active",
          educationalUse: ["practice", "instruction"],
        };
      case "quiz":
        return {
          learningResourceType: "Quiz",
          interactivityType: "active",
          educationalUse: ["assessment", "instruction"],
        };
      case "observation":
        return {
          learningResourceType: "Instructor-led demonstration",
          interactivityType: "mixed",
          educationalUse: ["instruction"],
        };
      default:
        // text / ai / その他は解説型
        return {
          learningResourceType: "テキスト解説",
          interactivityType: "expositive",
          educationalUse: ["instruction"],
        };
    }
  };

  const shape = shapeForType(lesson.type);
  const courseDisplayName = lesson.courseName || lesson.courseSlug;

  /*
  レッスンの author は編集チーム (Organization) だけにする。

  2026-09-11 にレッスン詳細の画面から監修者を下げた。画面に無いものを
  構造化データだけが主張する形は、検索エンジンに渡す情報として正しくない。
  **監修の事実そのものは消していない。** コース詳細と /authors には
  出たままなので、レッスン1本ずつの繰り返しだけが消える。
  */
  const authorSchema = getEditorialTeamOrgSchema();

  return {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    name: lesson.title,
    description:
      lesson.description ||
      `ゆめさくコードの${shape.learningResourceType}レッスン`,
    educationalLevel: "Beginner to Intermediate",
    learningResourceType: shape.learningResourceType,
    interactivityType: shape.interactivityType,
    educationalUse: shape.educationalUse,
    inLanguage: "ja",
    author: authorSchema,
    ...(lesson.datePublished && { datePublished: lesson.datePublished }),
    ...(lesson.dateModified && {
      dateModified: lesson.dateModified,
    }),
    isPartOf: {
      "@type": "Course",
      name: courseDisplayName,
      url: `${BASE_URL}/learn/courses/${lesson.courseSlug}`,
    },
    url: `${BASE_URL}/learn/courses/${lesson.courseSlug}/${lesson.lessonSlug}`,
    // og:image と同じものを出す。ここだけ別の絵を指すと、共有されたときと
    // 検索結果で違う絵になる
    image: lessonOgImageUrl({
      courseSlug: lesson.courseSlug,
      lessonSlug: lesson.lessonSlug,
      courseOgpImageUrl: lesson.courseOgpImageUrl,
      courseThumbnailUrl: lesson.courseThumbnailUrl,
    }),
    provider: organizationRef(),
    publisher: organizationRef(),
    ...(lesson.estimatedDuration && {
      timeRequired: `PT${lesson.estimatedDuration}M`,
    }),
  };
}

/**
 * TechArticle スキーマ
 * レッスンページ用 (LearningResource と併設)
 *
 * Google Rich Results が認識しやすい Article 派生型。
 * LearningResource とは観点が異なる (教材 vs 技術記事) ため、
 * 両方出力することで検索結果での露出を最大化する。
 *
 * proficiencyLevel は schema.org の許容値 (Beginner / Intermediate / Expert) に
 * 寄せる。course.difficultyLevel が "advanced" の場合は "Expert" にマップ。
 */
export function generateTechArticleSchema(params: {
  lesson: {
    title: string;
    description?: string | null;
    articleContent?: string | null;
    courseSlug: string;
    lessonSlug: string;
    datePublished?: string | null;
    dateModified?: string | null;
  };
  course?: {
    title?: string | null;
    difficultyLevel?: string | null;
  } | null;
  /** OGP image URL (なければ default og-image) */
  image?: string;
  /** 前提条件 (任意。prerequisites やレッスン要約から渡す) */
  dependencies?: string;
  /** 著者名 (省略時はゆめさくコード編集部) */
  authorName?: string;
}) {
  const { lesson, course, image, dependencies } = params;

  const proficiencyMap: Record<string, "Beginner" | "Intermediate" | "Expert"> =
    {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Expert",
    };
  const proficiencyLevel = course?.difficultyLevel
    ? proficiencyMap[course.difficultyLevel.toLowerCase()] || "Beginner"
    : "Beginner";

  const url = `${BASE_URL}/learn/courses/${lesson.courseSlug}/${lesson.lessonSlug}`;
  const wordCount = lesson.articleContent
    ? countArticleWords(lesson.articleContent)
    : 0;

  /*
  レッスンの author は編集チーム (Organization) だけにする。

  2026-09-11 にレッスン詳細の画面から監修者を下げた。画面に無いものを
  構造化データだけが主張する形は、検索エンジンに渡す情報として正しくない。
  **監修の事実そのものは消していない。** コース詳細と /authors には
  出たままなので、レッスン1本ずつの繰り返しだけが消える。
  */
  const authorSchema = getEditorialTeamOrgSchema();

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: lesson.title,
    description:
      lesson.description ||
      `${lesson.title}の解説とサンプルコードでステップごとに学べるチュートリアル記事。`,
    inLanguage: "ja",
    author: authorSchema,
    publisher: organizationRef(),
    image: image || DEFAULT_OG_IMAGE,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    url,
    proficiencyLevel,
  };

  if (lesson.datePublished) {
    schema.datePublished = lesson.datePublished;
  }
  if (lesson.dateModified || lesson.datePublished) {
    schema.dateModified = lesson.dateModified || lesson.datePublished;
  }
  if (course?.title) {
    schema.articleSection = course.title;
  }
  if (dependencies) {
    schema.dependencies = dependencies;
  }
  if (wordCount > 0) {
    schema.wordCount = wordCount;
  }

  return schema;
}

/**
 * Markdown / プレーンテキストの大まかな wordCount を算出。
 * 日本語は分かち書きできないので「全角文字 1 つ = 1 word」「半角は空白区切り」で
 * 概算する。schema.org の wordCount はあくまで参考値なので厳密さは不要。
 */
function countArticleWords(content: string): number {
  // Markdown 記法・コードフェンス・URL を雑に除去
  const stripped = content
    .replace(/```[\s\S]*?```/g, " ") // code fence
    .replace(/`[^`]*`/g, " ") // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // links → keep label
    .replace(/[#>*_~\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!stripped) return 0;

  // 全角 (CJK / かな) を 1 文字 = 1 word カウント
  const cjkMatches = stripped.match(/[぀-ヿ㐀-鿿ｦ-ﾟ]/g);
  const cjkCount = cjkMatches ? cjkMatches.length : 0;

  // 残った半角部分は空白区切りで word カウント
  const ascii = stripped.replace(/[぀-ヿ㐀-鿿ｦ-ﾟ]/g, " ");
  const asciiWords = ascii.split(/\s+/).filter((w) => w.length > 0);

  return cjkCount + asciiWords.length;
}

/**
 * HowTo スキーマ
 * 練習問題型レッスン (coding / sql / terminal) で「やってみよう」セクションが
 * ある場合のみ生成する。step は markdown 見出しや番号付きリストから抽出。
 *
 * 該当しない場合は null を返すので、呼び出し側で null チェックして
 * push するかどうか判断すること。
 */
export function generateHowToSchema(lesson: {
  title: string;
  type: string;
  articleContent?: string | null;
  description?: string | null;
  courseSlug: string;
  lessonSlug: string;
  estimatedDuration?: number | null;
}): object | null {
  // 練習問題型のみ対象
  const practiceTypes = new Set([
    "coding",
    "sql",
    "terminal",
    "code",
    "react",
    "vm",
    "prompt",
    "ai",
  ]);
  if (!practiceTypes.has(lesson.type)) {
    return null;
  }

  const content = lesson.articleContent || "";
  if (!content) {
    return null;
  }

  // 「やってみよう」セクションを抽出
  const steps = extractHowToSteps(content);
  if (steps.length === 0) {
    return null;
  }

  const url = `${BASE_URL}/learn/courses/${lesson.courseSlug}/${lesson.lessonSlug}`;
  // 「〜を実装する / 〜を解く」等で終わるタイトルは二重サフィックスを避ける
  const howToName = /(?:実装する|解く|作る|書く|やってみよう)$/.test(
    lesson.title,
  )
    ? lesson.title
    : `${lesson.title}を実装する`;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: howToName,
    description:
      lesson.description ||
      `${lesson.title}をステップごとに実装するための手順。`,
    inLanguage: "ja",
    url,
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
      url: `${url}#step-${index + 1}`,
    })),
  };

  if (lesson.estimatedDuration && lesson.estimatedDuration > 0) {
    schema.totalTime = `PT${lesson.estimatedDuration}M`;
  }

  return schema;
}

/**
 * articleContent から「やってみよう」セクション以降の step を抽出する。
 *
 * 抽出パターン (優先順):
 *   1. `### やってみよう` 以降の番号付きリスト (`1. ...`)
 *   2. `### やってみよう` 以降の見出し (`####` または `**Step 1: ...**`)
 *   3. 上記が無ければ空配列 (HowTo を出さない)
 */
function extractHowToSteps(
  content: string,
): Array<{ name: string; text: string }> {
  // 「やってみよう」見出し以降のセクションを切り出す
  const sectionMatch = content.match(
    /#{1,4}\s*やってみよう[\s\S]*?(?=\n#{1,3}\s|$)/,
  );
  if (!sectionMatch) return [];

  const section = sectionMatch[0];

  const steps: Array<{ name: string; text: string }> = [];

  // パターン 1: 番号付きリスト "1. ..." を step として扱う
  const numberedRegex = /^\s*(\d+)\.\s+(.+?)(?=\n\s*\d+\.\s+|\n\s*#|$)/gms;
  let m: RegExpExecArray | null;
  while ((m = numberedRegex.exec(section)) !== null) {
    const raw = m[2].trim();
    if (!raw) continue;
    // 1 行目を name、残りを text にする
    const lines = raw
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const name = stripMarkdownInline(lines[0]).slice(0, 110);
    const text = stripMarkdownInline(lines.join(" ")).slice(0, 500);
    if (name && text) {
      steps.push({ name, text });
    }
  }

  if (steps.length > 0) {
    return steps.slice(0, 10); // schema 肥大防止
  }

  // パターン 2: #### 見出し or **Step N: ...**
  const headingRegex =
    /(?:^#{4,}\s+(.+)$|^\*\*\s*(?:Step|ステップ)\s*\d+[:：]\s*(.+?)\*\*)/gim;
  while ((m = headingRegex.exec(section)) !== null) {
    const heading = (m[1] || m[2] || "").trim();
    if (!heading) continue;
    // 見出しの後ろの本文を 1 段落分取る
    const after = section.slice(m.index + m[0].length);
    const para = (after.match(/\n+([\s\S]*?)(?=\n#{1,4}\s|\n\*\*|$)/) || [
      "",
      "",
    ])[1].trim();
    const name = stripMarkdownInline(heading).slice(0, 110);
    const text = stripMarkdownInline(para || heading).slice(0, 500);
    if (name && text) {
      steps.push({ name, text });
    }
  }

  return steps.slice(0, 10);
}

function stripMarkdownInline(s: string): string {
  return s
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * ItemList スキーマ
 * 求人一覧ページ用
 */
export function generateJobListSchema(
  jobs: Array<{
    id: string;
    title: string;
    company: { name: string; slug?: string };
  }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "エンジニア求人一覧",
    description: "ゆめさくコードで学んだスキルを活かせるエンジニア求人情報",
    numberOfItems: jobs.length,
    itemListElement: jobs.slice(0, 10).map((job, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${BASE_URL}/jobs/${job.id}`,
      name: job.title,
    })),
  };
}

/**
 * DefinedTermSet スキーマ
 * 用語集ページ用
 */
export function generateDefinedTermSetSchema(term: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: `${term.name}とは`,
    description: term.description,
    url: term.url,
    inLanguage: "ja",
    hasDefinedTerm: {
      "@type": "DefinedTerm",
      name: term.name,
      description: term.description,
      url: term.url,
    },
  };
}

/**
 * 構造化データをHTMLに埋め込む形式で返す
 */
export function structuredDataScript(data: object | object[]) {
  const jsonData = Array.isArray(data) ? data : [data];

  return {
    type: "application/ld+json",
    children: safeJsonLdStringify(jsonData),
  };
}
